import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, ValidationErrors } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ActionItem, ActionItemModifier, ActionItemStyle, IconSize } from '@stylehaven/components';
import { ParseScanItem } from '../../../helpers';
import { GetShortRecordIdPipe } from '../../../record/pipes/get-short-record-id.pipe';
import { ScanItem, ScanItemState } from '../../entities/scan-item.entity';

@Component({
    standalone: false,
    selector: 'digihaven-scanner-input',
    templateUrl: './scanner-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScannerInputComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly destroy$: Subject<void> = new Subject<void>();

    readonly scanItems$: BehaviorSubject<ScanItem[]> = new BehaviorSubject<ScanItem[]>([]);
    readonly scanItemState: typeof ScanItemState = ScanItemState;

    isInvalid$!: Observable<boolean>;

    errorMsgs$!: Observable<string[]>;

    recordListControl!: UntypedFormControl;

    @ViewChild('scannerInput') input!: ElementRef<HTMLInputElement>;

    @Input() set items(items: ScanItem[]) {
        if (items) {
            this.scanItems$.next(items);
        }
    }

    @Input() initialFocus = true;
    @Input() required = false;
    @Input() icon = 'add';
    @Input() autoClear = true;

    @Output() scanned: EventEmitter<ScanItem> = new EventEmitter();
    @Output() remove: EventEmitter<string> = new EventEmitter();

    constructor(
        private getShortRecordIdPipe: GetShortRecordIdPipe,
        private readonly translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.initStateStreams();
    }

    ngOnDestroy(): void {
        this.scanItems$.complete();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        if (this.initialFocus) {
            this.focus();
        }
    }

    focus(): void {
        this.input.nativeElement.focus();
    }

    enable(): void {
        this.input.nativeElement.disabled = false;
    }

    disable(): void {
        this.input.nativeElement.disabled = true;
    }

    clear(): void {
        this.input.nativeElement.value = '';
    }

    setErrors(errors: ValidationErrors | null): void {
        this.recordListControl.setErrors(errors);
    }

    removeItem(item: ScanItem): void {
        this.remove.emit(item.id);
    }

    handleItem(): void {
        const scanItem: ScanItem = ParseScanItem(this.recordListControl.value);

        if (this.autoClear) {
            this.recordListControl.reset();
        }

        if (!scanItem.id?.length) {
            return;
        }

        if (this.scanItems$.value.find(value => value.id === scanItem.id)) {
            this.recordListControl.setErrors({
                message: this.translateService.instant('link-records.form.error.already-added')
            });
            return;
        }

        this.scanned.emit(scanItem);
    }

    getTagClass(item: ScanItem): string {
        switch (item.state) {
            case ScanItemState.Loading:
                return 'sh-tag--subtle';
            case ScanItemState.Rejected:
                return 'sh-tag--error';
            case ScanItemState.Accepted:
            default:
                return 'sh-tag--brand';
        }
    }

    private initForm() {
        this.recordListControl = new UntypedFormControl();
    }

    private initStateStreams() {
        this.isInvalid$ = combineLatest([this.recordListControl.statusChanges, this.scanItems$]).pipe(
            map(([status, items]) => status === 'INVALID' || items.some(item => item.state === ScanItemState.Rejected)),
            takeUntil(this.destroy$)
        );

        this.errorMsgs$ = combineLatest([this.recordListControl.statusChanges, this.scanItems$]).pipe(
            map(([_, items]) => items),
            map(items =>
                items.map(item => item.errorMsg && `${this.getShortRecordIdPipe.transform(item.id)}: ${item.errorMsg}`)
            ),
            map(items => [this.recordListControl.getError('message'), ...items]),
            map(errors => errors.filter(error => !!error))
        );
    }

    get addActionItem(): ActionItem {
        return {
            label: '',
            icon: this.icon,
            iconSize: IconSize.REGULAR,
            style: ActionItemStyle.PureIconButton,
            modifiers: [ActionItemModifier.ActionInInputField],
            eventListener: () => {
                this.handleItem();
                this.focus();
            }
        };
    }
}
